import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import BadgePill from './BadgePill';
import BadgeColorPicker from './BadgeColorPicker';
import ProductBadge from 'data/ProductBadge';
import { validateBadgeForm, hasErrors, normalizeBadgeName, BadgeFormErrors } from 'utils/ProductBadges';
import {
  ADD_NEW_BADGE_TITLE,
  BADGE_TEXT_LABEL,
  BADGE_COLOR_LABEL,
  BADGE_PREVIEW_LABEL,
  GENERATE_PREVIEW_TEXT,
  BADGE_PREVIEW_REQUIRED,
  BADGE_PREVIEW_STALE,
  BADGE_COLOR_REQUIRED
} from 'constants/productBadges';
import { Main, Border, Text } from 'styles/themeColors';
import { FontSize, FontWeight } from 'lib/styles';

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: ${FontSize.semi};
  font-weight: ${FontWeight.bold};
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: ${Text.primary};
`;

const Asterisk = styled.span`
  color: ${Main.error};
  margin-left: 2px;
`;

const Divider = styled.div`
  width: 1px;
  align-self: stretch;
  background-color: ${Border.light};
  margin: 0 24px;
`;

const PreviewSlot = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: ${Main.error};
`;

interface Props {
  badges: ProductBadge[];
  isSaving: boolean;
  onSave: (name: string, colorHash: string) => void;
}

const AddBadgeForm = forwardRef<{ save: () => void }, Props>(
  ({ badges, isSaving, onSave }, ref) => {
    const [name, setName] = useState('');
    const [colorHash, setColorHash] = useState('');
    const [errors, setErrors] = useState<BadgeFormErrors>({});
    const [previewError, setPreviewError] = useState('');
    const [preview, setPreview] = useState<{ name: string; colorHash: string } | null>(null);

    const isPreviewCurrent =
      !!preview
      && preview.name === normalizeBadgeName(name)
      && preview.colorHash === colorHash;

    const handleTextChange = (value: string) => {
      setName(value);
      setPreviewError('');
      setErrors(current => ({ ...current, name: undefined }));
    };

    const handleColorChange = (nextColorHash: string) => {
      setColorHash(nextColorHash);
      setPreviewError('');
      setErrors(current => ({ ...current, colorHash: undefined }));
    };

    const handleGeneratePreview = () => {
      const nextErrors = validateBadgeForm(name, colorHash, badges);
      setErrors(nextErrors);
      setPreviewError('');

      if (hasErrors(nextErrors)) {
        setPreview(null);
        return;
      }

      setPreview({ name: normalizeBadgeName(name), colorHash });
    };

    const save = () => {
      const nextErrors = validateBadgeForm(name, colorHash, badges);
      setErrors(nextErrors);

      if (hasErrors(nextErrors)) return;

      if (!preview) {
        setPreviewError(BADGE_PREVIEW_REQUIRED);
        return;
      }

      if (!isPreviewCurrent) {
        setPreviewError(BADGE_PREVIEW_STALE);
        return;
      }

      setPreviewError('');
      onSave(name.trim(), colorHash);
    };

    useImperativeHandle(ref, () => ({ save }));

    return (
      <>
        <SectionTitle>{ADD_NEW_BADGE_TITLE}</SectionTitle>

        <FlexboxContainer flexDirection="row" alignItems="flex-start" width="100%">
          <FlexboxContainer flexDirection="column" width="440px" alignItems="flex-start">
            <Input
              id="badge-text"
              labelName={BADGE_TEXT_LABEL}
              labelPosition="top"
              placeholder="Enter badge text"
              value={name}
              onInputChange={handleTextChange}
              width="100%"
              displayAsterisk
              isInvalid={!!errors.name}
              errorMessage={errors.name}
              disabled={isSaving}
            />
          </FlexboxContainer>

          <FlexboxContainer flexDirection="column" alignItems="flex-start" margin="0 0 0 40px" width="auto">
            <FieldLabel>
              {BADGE_COLOR_LABEL}<Asterisk>*</Asterisk>
            </FieldLabel>
            <BadgeColorPicker value={colorHash} onChange={handleColorChange} disabled={isSaving} />
            {errors.colorHash ? <ErrorText>{BADGE_COLOR_REQUIRED}</ErrorText> : null}
          </FlexboxContainer>

          <Divider />

          <FlexboxContainer flexDirection="column" alignItems="flex-start" width="auto">
            <FieldLabel>{BADGE_PREVIEW_LABEL}</FieldLabel>
            <PreviewSlot>
              {preview ? <BadgePill name={preview.name} colorHash={preview.colorHash} /> : null}
            </PreviewSlot>
          </FlexboxContainer>
        </FlexboxContainer>

        <FlexboxContainer flexDirection="column" alignItems="flex-start" width="auto" margin="16px 0 0 0">
          <Button
            isGhost
            padding="0 24px"
            theme="brandDark"
            color={Main.brandDark}
            borderColor={Main.brandDark}
            onClick={handleGeneratePreview}
            disabled={isSaving}
          >
            {GENERATE_PREVIEW_TEXT}
          </Button>
          {previewError ? <ErrorText>{previewError}</ErrorText> : null}
        </FlexboxContainer>
      </>
    );
  }
);

export default AddBadgeForm;